import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Form, Input, message, Modal, Space, Spin, Table, Typography, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, HomeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import {
  getHotelAddressList,
  createHotelAddress,
  updateHotelAddress,
  deleteHotelAddress,
} from 'src/features/address/hotelAddress.api';
import { HotelAddress, HotelAddressCreate, HotelAddressUpdate } from 'src/features/address/hotelAddress.types';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import { HotelAddressDeleteResponse } from 'src/features/address/hotelAddress.types';

const CompanyHotelAddressTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [addressList, setAddressList] = useState<HotelAddress[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState<HotelAddress | null>(null);

  const fetchAddressList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getHotelAddressList({ companyId });
      if (response.status === 200) {
        const transformedAddress = response.data.address.map((item: any) => ({
          id: item.id,
          company_id: item.companyId,
          name: item.name,
          address_city: item.addressCity,
          address_city_id: item.addressCityId,
          address_house: item.addressHouse,
          latitudo: item.latitudo,
          longitudo: item.longitudo,
        }));
        setAddressList(transformedAddress);
      } else {
        message.error('Ошибка загрузки адресов отеля');
      }
    } catch (error) {
      console.error('Failed to fetch hotel addresses:', error);
      message.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchAddressList();
  }, [fetchAddressList]);

  const handleAdd = () => {
    setEditingAddress(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: HotelAddress) => {
    setEditingAddress(record);
    form.setFieldsValue({
      name: record.name,
      address_city: record.address_city,
      address_city_id: record.address_city_id,
      address_house: record.address_house,
      latitudo: String(record.latitudo),
      longitudo: String(record.longitudo),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = (await deleteHotelAddress({ companyId, addressId: id })) as {
        status: number;
        data: HotelAddressDeleteResponse;
      };
      if (response.status === 200 && response.data) {
        message.success('Адрес отеля удален');
        fetchAddressList();
      } else {
        message.error('Адрес отеля не найден');
      }
    } catch (error) {
      console.error('Failed to delete hotel address:', error);
      message.error('Ошибка удаления');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingAddress) {
        const updateData: HotelAddressUpdate = {
          id: editingAddress.id,
          name: values.name,
          address_city: values.address_city,
          address_house: values.address_house,
          latitudo: parseFloat(values.latitudo),
          longitudo: parseFloat(values.longitudo),
          address_city_id: values.address_city_id,
        };
        const response = await updateHotelAddress({ companyId, data: updateData });
        if (response.status === 200) {
          message.success('Адрес отеля обновлен');
          setModalVisible(false);
          fetchAddressList();
        }
      } else {
        const createData: HotelAddressCreate = {
          name: values.name,
          address_city: values.address_city,
          address_house: values.address_house,
          latitudo: values.latitudo,
          longitudo: values.longitudo,
          address_city_id: values.address_city_id,
        };
        const response = await createHotelAddress({ companyId, data: createData });
        if (response.status === 201) {
          message.success('Адрес отеля создан');
          setModalVisible(false);
          fetchAddressList();
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      message.error('Ошибка сохранения');
    }
  };

  const truncateText = (text: string | null, maxLength = 10) => {
    if (!text) return text;
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const columns: ColumnsType<HotelAddress> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => {
        const truncated = truncateText(text, 15);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <strong>{truncated}</strong>
            </Tooltip>
          );
        }
        return <strong>{text}</strong>;
      },
    },
    {
      title: 'Город',
      dataIndex: 'address_city',
      key: 'address_city',
      render: (text: string) => {
        const truncated = truncateText(text, 10);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <span>{truncated}</span>
            </Tooltip>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: 'Дом/здание',
      dataIndex: 'address_house',
      key: 'address_house',
      render: (text: string) => {
        const truncated = truncateText(text, 10);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <span>{truncated}</span>
            </Tooltip>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: 'Широта',
      dataIndex: 'latitudo',
      key: 'latitudo',
      render: (text: number) => <span>{text.toFixed(6)}</span>,
    },
    {
      title: 'Долгота',
      dataIndex: 'longitudo',
      key: 'longitudo',
      render: (text: number) => <span>{text.toFixed(6)}</span>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size='small' icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Редактировать
          </Button>
          <Popconfirm
            title='Удалить адрес отеля?'
            onConfirm={() => handleDelete(record.id)}
            okText='Да'
            cancelText='Нет'
          >
            <Button size='small' danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4}>
          <HomeOutlined style={{ marginRight: 8 }} />
          Адреса отелей
        </Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Добавить адрес
          </Button>
        </ComponentRoleProtected>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={addressList}
          rowKey='id'
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Нет адресов отелей' }}
        />
      </Spin>

      <Modal
        title={editingAddress ? 'Редактировать адрес отеля' : 'Добавить адрес отеля'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        okText='Сохранить'
        cancelText='Отмена'
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label='Название'
            rules={[
              { required: true, message: 'Введите название адреса' },
              { max: 128, message: 'Название не более 128 символов' },
            ]}
          >
            <Input placeholder='Например: Центральный отель' />
          </Form.Item>

          <Form.Item
            name='address_city'
            label='Город'
            rules={[
              { required: true, message: 'Введите город' },
              { max: 256, message: 'Город не более 256 символов' },
            ]}
          >
            <Input placeholder='Например: Москва' />
          </Form.Item>

          <Form.Item
            name='address_city_id'
            label='ID города'
            rules={[{ max: 64, message: 'ID города не более 64 символов' }]}
          >
            <Input placeholder='Например: 0c5b2444-70a0-4932-980c-b4dc0d3f02b5' />
          </Form.Item>

          <Form.Item
            name='address_house'
            label='Дом/здание'
            rules={[
              { required: true, message: 'Введите номер дома или здания' },
              { max: 256, message: 'Номер дома не более 256 символов' },
            ]}
          >
            <Input placeholder='Например: ул. Тверская, д. 10' />
          </Form.Item>

          <Form.Item
            name='latitudo'
            label='Широта'
            rules={[
              { required: true, message: 'Введите широту' },
              { pattern: /^-?\d+(\.\d+)?$/, message: 'Введите корректное число' },
              { max: 16, message: 'Широта не более 16 символов' },
            ]}
          >
            <Input placeholder='Например: 55.7558' />
          </Form.Item>

          <Form.Item
            name='longitudo'
            label='Долгота'
            rules={[
              { required: true, message: 'Введите долготу' },
              { pattern: /^-?\d+(\.\d+)?$/, message: 'Введите корректное число' },
              { max: 16, message: 'Долгота не более 16 символов' },
            ]}
          >
            <Input placeholder='Например: 37.6173' />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CompanyHotelAddressTab;
